import {
  action,
  computed,
  makeObservable,
  observable,
  runInAction
} from 'mobx';

import { AppNotificationType } from 'shared/entities/appNotifications';
import { apiUrls } from 'shared/entities/domain';
import {
  normalizeBusinessHoursSettings,
  BusinessHoursSaveTarget
} from 'shared/entities/projectSettings';
import type {
  BusinessHoursErrorCode,
  ProjectSettingsServer
} from 'shared/entities/projectSettings';
import type { IRootStore } from 'shared/entities/store/rootStore';
import { LoadingStageModel } from 'shared/models/loadingStage';

import BusinessHoursSettingsModel from './BusinessHoursSettingsModel';

export default class BusinessHoursModel {
  settings: BusinessHoursSettingsModel | null = null;
  error: BusinessHoursErrorCode | null = null;
  readonly loadingStage: LoadingStageModel = new LoadingStageModel();
  readonly savingStage: LoadingStageModel = new LoadingStageModel();

  private readonly _rootStore: IRootStore;
  private _hasSavedConfiguration = false;

  constructor(rootStore: IRootStore) {
    this._rootStore = rootStore;

    makeObservable<BusinessHoursModel, '_hasSavedConfiguration'>(this, {
      settings: observable,
      error: observable,
      _hasSavedConfiguration: observable,

      canSave: computed,
      isConfigured: computed,

      setProjectSettings: action.bound,
      changeEnabled: action.bound,
      reload: action.bound,
      save: action.bound,
      reset: action.bound
    });
  }

  get canSave(): boolean {
    return !!this.settings?.canSave && !this.savingStage.isLoading;
  }

  get isConfigured(): boolean {
    return this._hasSavedConfiguration;
  }

  setProjectSettings(data: ProjectSettingsServer): void {
    const error = data.business_hours_error ?? null;

    if (error) {
      this._hasSavedConfiguration = false;
      this.setError(error);
      return;
    }

    this._hasSavedConfiguration = !!data.business_hours;
    this.setSettings(
      data.business_hours
        ? new BusinessHoursSettingsModel(
            normalizeBusinessHoursSettings(data.business_hours)
          )
        : BusinessHoursSettingsModel.fromDefaultParams()
    );
  }

  async changeEnabled(value: boolean): Promise<void> {
    if (!this.settings || this.savingStage.isLoading) {
      return;
    }

    this.settings.changeEnabled(value);

    if (!value || this._hasSavedConfiguration) {
      await this.save();
    }
  }

  reload = async (): Promise<void> => {
    if (this.loadingStage.isLoading) {
      return;
    }

    const projectId = this._rootStore.projectId;
    if (!projectId) {
      return;
    }

    this.loadingStage.loading();
    const response =
      await this._rootStore.networkStore.api<ProjectSettingsServer>(
        apiUrls.PROJECTS_LIST_SETTINGS
      );

    if (projectId !== this._rootStore.projectId) {
      return;
    }

    if (response.isError) {
      this.loadingStage.error();
      return;
    }

    runInAction(() => {
      if (!this.settings?.touched) {
        this.setProjectSettings(response.data);
      } else {
        this.error = null;
      }
      this.loadingStage.success();
    });
  };

  save = async (
    target: BusinessHoursSaveTarget = BusinessHoursSaveTarget.schedule
  ): Promise<void> => {
    const businessHours = this.settings;
    if (!businessHours || this.savingStage.isLoading) {
      return;
    }

    const canSave =
      target === BusinessHoursSaveTarget.schedule
        ? businessHours.canSave
        : businessHours.autoReplyCanSave;
    if (!canSave) {
      return;
    }

    if (
      target === BusinessHoursSaveTarget.schedule &&
      !businessHours.validateSchedule()
    ) {
      return;
    }

    const projectId = this._rootStore.projectId;
    if (!projectId) {
      return;
    }

    const saveAutoReplyDraft =
      target === BusinessHoursSaveTarget.schedule &&
      businessHours.autoReplyCanSave;

    this.savingStage.loading();
    const settings = businessHours.toJSON(
      target === BusinessHoursSaveTarget.schedule
        ? { withSavedAutoReply: !saveAutoReplyDraft }
        : { withSavedSchedule: true }
    );
    const response = await this._rootStore.networkStore.api(
      apiUrls.PROJECTS_SET_SETTINGS,
      {
        method: 'POST',
        data: { business_hours: settings }
      }
    );

    if (projectId !== this._rootStore.projectId) {
      return;
    }

    runInAction(() => {
      if (response.isError) {
        this.savingStage.error();
        const notificationKey =
          target === BusinessHoursSaveTarget.autoReply
            ? 'SettingsStore.notifications.autoReplyError'
            : 'SettingsStore.notifications.businessHoursError';
        this._rootStore.appNotificationsStore.open({
          title: (t) => t(notificationKey, { ns: 'stores' }),
          type: AppNotificationType.error
        });
        return;
      }

      if (this.settings === businessHours) {
        if (target === BusinessHoursSaveTarget.schedule) {
          businessHours.commitSchedule();
          if (saveAutoReplyDraft) {
            businessHours.commitAutoReply();
          }
          this._hasSavedConfiguration = true;
        } else {
          businessHours.commitAutoReply();
        }
      }
      this.savingStage.success();
      const notificationKey =
        target === BusinessHoursSaveTarget.autoReply
          ? 'SettingsStore.notifications.autoReplySuccess'
          : 'SettingsStore.notifications.businessHoursSuccess';
      this._rootStore.appNotificationsStore.open({
        title: (t) => t(notificationKey, { ns: 'stores' }),
        type: AppNotificationType.success
      });
    });
  };

  reset(): void {
    this.settings = null;
    this.error = null;
    this._hasSavedConfiguration = false;
    this.loadingStage.reset();
    this.savingStage.reset();
  }

  private setSettings(settings: BusinessHoursSettingsModel | null): void {
    this.settings = settings;
    this.error = null;
  }

  private setError(error: BusinessHoursErrorCode): void {
    this.settings = null;
    this.error = error;
  }
}
